import * as moment from 'moment';
import { languages } from '../../i18n';

type Config = {
    timezone: number;
    isDST: boolean;
    pattern: string;
};

export class Time {
    private config: Config = null;

    // set formatter config
    set = (config: Config) => {
        this.config = config;
    };

    // 获取当前时区
    get timezone() {
        return this.config.timezone;
    }

    // 获取当前format
    get format() {
        return this.config.pattern;
    }

    // 获取当前平台时区对应时间
    getPlatformValue = (pattern: string) => {
        const { timezone, isDST } = this.config;
        return moment()
            .utc()
            .utcOffset(timezone + (isDST ? 1 : 0))
            .format(pattern);
    };

    // get moment value
    getTimeValue = (type: 'year' | 'month' | 'date' | 'hour' | 'minute' | 'second') => {
        const { timezone, isDST } = this.config;
        return moment()
            .utc()
            .utcOffset(timezone + (isDST ? 1 : 0))
            .get(type);
    };

    // get ths formatted value by pattern
    pattern = (value: any, pattern: string) => {
        const { timezone, isDST } = this.config;
        return moment(value)
            .add(timezone + (isDST ? 1 : 0), 'h')
            .format(pattern);
    };
    // get the formatted value
    value = (value: any, format?: any) => {
        const { timezone, isDST, pattern } = this.config;
        return moment(value)
            .add(timezone + (isDST ? 1 : 0), 'h')
            .format(format || pattern);
    };
    // get the revert formatted value
    revert = (value: any, format?: any) => {
        const { timezone, isDST, pattern } = this.config;
        return moment(value)
            .subtract(timezone + (isDST ? 1 : 0), 'h')
            .format(format || pattern);
    };

    // revert with week
    revertWithDate = (time: string, day: number, format: string) => {
        const { timezone, isDST, pattern } = this.config;
        const value = moment(time, format || pattern);
        let week = Number(day);
        const lang = languages.getDefault();
        if (lang === 'zh-TW' || lang === 'ko-KR' || lang === 'ja-JP') {
            week = week + 1;
        }
        const isoTime = moment(value)
            .set('weekday', week)
            .subtract(timezone + (isDST ? 1 : 0), 'h');
        return [isoTime.format(format || pattern), (isoTime.get('day') + 6) % 7];
    };

    // add with week
    addWithDate = (time: string, day: number, format: string) => {
        const value = moment(time, 'HH:mm');
        const { timezone, isDST, pattern } = this.config;
        let week = Number(day);
        const lang = languages.getDefault();
        if (lang === 'zh-TW' || lang === 'ko-KR' || lang === 'ja-JP') {
            week = week + 1;
        }
        const isoTime = moment(value)
            .set('weekday', week)
            .add(timezone + (isDST ? 1 : 0), 'h');
        return [isoTime.format(format || pattern), (isoTime.get('day') + 6) % 7];
    };

    start = (
        value: any,
        type: 'year' | 'month' | 'quarter' | 'week' | 'isoWeek' | 'day' | 'hour' | 'minute' | 'second' = 'day',
    ) => {
        return moment(value).startOf(type);
    };

    end = (
        value: any,
        type: 'year' | 'month' | 'quarter' | 'week' | 'isoWeek' | 'day' | 'hour' | 'minute' | 'second' = 'day',
    ) => {
        return moment(value).endOf(type);
    };

    // moment化时间
    getValue = (value: any, format?: any) => {
        const { pattern } = this.config;
        return moment(value).format(format || pattern);
    };

    // 转化unix时间戳
    unix = (value: any, format?: any) => {
        const { pattern } = this.config;
        return moment
            .unix(value)
            .utc()
            .format(format || pattern);
    };

    // 获取昨天日期
    getYesterday = (format: any) => {
        return moment().subtract(1, 'days').format(format);
    };

    // 获取当前日期
    getToday = () => {
        return moment();
    };

    // Millisecond counterpart of `value()` — for comparison/arithmetic, never for display
    // `value()` 的毫秒版本，用于比较/计算，不用于展示
    // Avoids the `new Date(value(v))` round-trip, which yields Invalid Date when the
    // platform-configured `pattern` is not ISO-like (e.g. 'DD / MM / YYYY HH:mm:ss')
    // 避免 `new Date(value(v))` 的字符串往返：平台配置的 pattern 非 ISO 格式时会解析成 Invalid Date
    timestamp = (value: any): number => {
        const { timezone, isDST } = this.config;
        return moment(value)
            .add(timezone + (isDST ? 1 : 0), 'h')
            .valueOf();
    };
}

export default new Time();
